import { useUnit } from 'effector-react';
import _, { debounce, isEmpty } from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global } from 'models/store-global';
import { useEffect, useRef, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

import HText from 'components/HText';
import HookFormControl from 'components/HookFormControl';
import HookCountryFormControl from 'components/HookFormControl/Select/Country/HookCountryFormControl';
import HookTextFormControl from 'components/HookFormControl/Text/HookTextFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import PopUpWrapper from 'components/StyledTemplates/PopUpWrapper';
import { Button, Checkbox, Frame, H1, P } from 'components/StyledTemplates/StyledTemplates';
import {
    CheckboxUpdateFrame,
    Divider,
    DocumentNameFrame,
    NotificationsPlate,
    NotificationsPlateHeader,
    NotifyShareRow,
    RoleFrame,
    StatusFrame,
    SupportHeading,
} from 'components/pages/Dashboard/common/CommunityPageStyles';

import DateService from 'services/DateService';
import EstatePlanService from 'services/EstatePlanService';
import ModalsService from 'services/ModalsService';
import ParticipantsService from 'services/ParticipantsService';
import { phoneToBackendFormat } from 'services/Utils';
import { isna, objectDiff, objectFlatten, objectNested } from 'utils/common-helper';

import useAsyncFunction from 'hooks/useAsyncFunction';
import useModal from 'hooks/useModal';
import useResources from 'hooks/useResources';

import { type AddressSuggestion } from 'types/zod/address-suggestion';
import { type Participant } from 'types/zod/participant';

const UpdateParticipantModal = () => {
    const form = useForm();
    const { handleSubmit, setValue, getValues, watch, reset } = form;
    watch();
    const data = getValues() as any;

    const { t } = useResources();

    const [isAdvisor, setIsAdvisor] = useState(false);
    const [isUsResident, setUsResident] = useState(true);

    const [addressSuggestions, setAddressSuggestions] = useState([] as AddressSuggestion[]);

    const [addressWasUpdated, setAddressWasUpdated] = useState(false);
    useModal('UPDATE_PARTICIPANT', {
        onOpen: (participant: Participant) => {
            handlers.populateForm(participant);
            setAddressWasUpdated(false);
            const isUsResident =
                !participant.address?.domicile?.country_code || participant.address?.domicile?.country_code === 'USA';
            setUsResident(isUsResident);
            form.setValue(`us_resident`, isUsResident);
        },
    });

    const tab = useUnit($global.map((state) => state.temp?.selected_community_participant));
    const documents = useUnit($global.map((state) => state.community?.docs || []));
    const currentParticipant = useUnit($$auth.$currentParticipant);
    const isOriginator = currentParticipant?.participant_id === data.participant_id;
    const allowNonUs = !isOriginator && !data.isSpouse;

    const isParticipantNotifiable = ParticipantsService.isNotifiableParticipant(data);

    const allNotifySelected = data?.roles?.every?.(
        (r: any) => r.notify || (!ParticipantsService.isNotifiableRole(data, r) && isParticipantNotifiable),
    );
    const allShareSelected = data?.roles?.every?.(
        (r: any) =>
            r.visibility_code === 'full_doc' ||
            (!ParticipantsService.isNotifiableRole(data, r) && isParticipantNotifiable),
    );

    const handlers = {
        selectAllNotify: () => {
            const newValue = !allNotifySelected;
            data.roles.forEach((r: any, index: number) => {
                if (ParticipantsService.isNotifiableRole(data, r)) {
                    setValue(`roles[${index}].notify`, newValue);
                }
            });
        },
        selectAllShare: () => {
            data.roles.forEach((r: any, index: number) => {
                if (!ParticipantsService.isNotifiableRole(data, r)) {
                    return;
                }
                setValue(`roles[${index}].visibility_code`, allShareSelected ? 'participation_only' : 'full_doc');
                if (!allShareSelected) {
                    setValue(`roles[${index}].notify`, true);
                }
            });
        },
        search: async (e: any) => {
            if (e.target.value) {
                const result = await $$api.participants.lookupAddressesFx({ search: e.target.value });
                setAddressSuggestions(result.suggestions || []);
            }
        },
        useSuggestedAddress: (value: string, item: AddressSuggestion) => {
            if (value) {
                setValue('address.domicile.address_line1', item.street_line);
                setValue('address.domicile.address_line2', item.secondary);
                setValue('address.domicile.city', item.city);
                setValue('address.domicile.state_code', item.state);
                setValue('address.domicile.zip', item.zipcode);
            }
        },
        getFullAddressLine: (item: AddressSuggestion) => {
            return [item.street_line, item.secondary, item.city, item.state, item.zipcode].join(' ');
        },
        updateRelationship: (event: any) => {
            const d = objectNested(getValues());
            handlers.populateForm(d);
            setValue('observer_relationship', event.target.value);
        },
        updateAge: () => {
            const dob = getValues('date_of_birth');
            const approxAge = +getValues('approx_age') || 0;
            setValue('age', dob ? DateService.getAge(dob) : Math.floor(approxAge / 365));
            setValue('date_of_birth', dob);
            setValue('approx_age', approxAge);
        },
        validateApproxAge: () => {
            return (
                (isna(getValues()?.date_of_birth) &&
                    isna(getValues()?.approx_age) &&
                    t('common.widget.participant.dobOrApproxAgeRequired')) ||
                true
            );
        },
        validateEmail: () => {
            return (
                (isna(getValues()?.email) &&
                    isna(getValues()?.phone_number) &&
                    (data?.age > 13 || data?.approx_age > 0) &&
                    t('common.widget.participant.emailOrPhoneRequired')) ||
                true
            );
        },
        validatePhoneNumber: () => {
            return (
                (isna(getValues()?.email) &&
                    isna(getValues()?.phone_number) &&
                    (data?.age > 13 || data?.approx_age > 0) &&
                    t('common.widget.participant.emailOrPhoneRequired')) ||
                true
            );
        },
        validateDateOfBirth: () => {
            return (
                (isna(getValues()?.date_of_birth) &&
                    isna(getValues()?.approx_age) &&
                    t(
                        data.isSpouse
                            ? 'common.widget.participant.dobRequired'
                            : 'common.widget.participant.dobOrApproxAgeRequired',
                    )) ||
                true
            );
        },
        onNotifyClick: (index: number) => () => {
            setValue(`roles[${index}].notify`, !(_.get(data, `roles[${index}].notify`) === true));
        },
        onShareClick: (index: number) => () => {
            setValue(
                `roles[${index}].visibility_code`,
                getValues(`roles[${index}].visibility_code`) === 'participation_only'
                    ? 'full_doc'
                    : 'participation_only',
            );
            if (getValues(`roles[${index}].visibility_code`) === 'full_doc') {
                setValue(`roles[${index}].notify`, true);
            }
        },
        onCancelClick: () => {
            handlers.populateForm({});
            ModalsService.hideModal('UPDATE_PARTICIPANT');
        },
        onClickOutside: () => {
            reset();
            handlers.populateForm({});
        },
        onDateOfBirthChange: () => {
            setValue('approx_age', null);
            handlers.updateAge();
        },
        onFormChange: (e: any) => {
            if (e?.target?.name?.includes?.('address') || e?.target?.attributes?.getNamedItem?.('aria-autocomplete')) {
                setAddressWasUpdated(!_.isEmpty(_.pickBy(getValues('address.domicile'), _.identity)));
            }
        },
        onApproxAgeChange: () => {
            setValue('date_of_birth', null);
            setValue('date_of_birth_US', null);
            handlers.updateAge();
        },
        populateForm: (participant: any) => {
            Object.entries(objectFlatten(participant)).forEach(([key, value]) => {
                setValue(key, value);
            });
        },
        onUsResidentClick: () => {
            const isChecked = !form.getValues(`us_resident`);
            setUsResident(isChecked);
            if (isChecked) {
                form.setValue(`address.domicile.country_code`, 'USA');
            } else {
                form.setValue(`address.domicile.country_code`, '');
                form.setValue(`address.domicile.state_code`, null);
            }
            setAddressWasUpdated(true);
        },
    };

    useEffect(() => {
        if (data?.observer_relationship) {
            setIsAdvisor(ParticipantsService.isAdvisor(data));
        } else {
            setIsAdvisor(false);
        }
    }, [data?.observer_relationship]);

    useEffect(() => {
        if (isEmpty(data) || data.last_name === '') {
            return;
        }
        //reset();
        if (data.isSpouse) {
            delete data.approx_age;
            form.setValue('approx_age', null);
        }
        if (data.approx_age) {
            delete data.date_of_birth;
            form.setValue('date_of_birth', null);
        }
        if (data.date_of_birth) {
            delete data.approx_age;
            form.setValue('approx_age', null);
        }
        //handlers.populateForm(data);
    }, [JSON.stringify(data)]);

    const { execute: onSubmit, loading } = useAsyncFunction(async (data: any) => {
        data = objectNested(data);
        handlers.populateForm(data);
        if (!addressWasUpdated || !data.address?.domicile?.address_line1) {
            delete data?.address;
        }
        if (data.phone_number) {
            data.phone_number = phoneToBackendFormat(data.phone_number);
        }
        const realm = $global.map((state) => state.community?.realm).getState();
        const oldData = realm?.find?.((i: any) => i?.participant_id === data?.participant_id);
        const changes = objectDiff(oldData, data);
        if (_.isEmpty(_.pickBy(changes?.incoming?.address?.domicile, _.identity))) {
            data = _.omit(data, ['address']);
        }
        if (changes?.incoming?.date_of_birth || data?.approx_age == null || data?.approx_age === '') {
            data = _.omit(data, ['approx_age']);
        } else {
            if (changes?.incoming?.approx_age != null) {
                data = _.omit(data, ['date_of_birth']);
            }
        }
        if (!isParticipantNotifiable) {
            data.roles.forEach((r: any, index: number) => {
                if (r.notifiable) {
                    setValue(`roles[${index}].notify`, false);
                    setValue(`roles[${index}].visibility_code`, 'participation_only');
                }
            });
        }
        if (data?.address?.domicile?.address_line1) {
            if (!data.address.domicile.country_code) {
                data.address.domicile.country_code = 'USA';
            }
            if (data.address.domicile.country_code === 'USA') {
                delete data.address.domicile.region;
            } else {
                delete data.address.domicile.state_code;
            }
        }
        if (data?.participant_id) {
            await $$api.participants.updateParticipantFx(data);
        } else {
            if (!data.date_of_birth) {
                data.date_of_birth = '1980-01-01';
            }
            await $$api.participants.createParticipantFx(data);
        }
        await EstatePlanService.loadEstatePlan();
        handlers.populateForm({});
        ModalsService.hideModal('UPDATE_PARTICIPANT');
    });

    const delayedSearch = useRef(debounce(handlers.search, 500)).current;

    const headerText = data?.participant_id
        ? `${data?.first_name} ${data?.last_name}`
        : t('communityPagePopUp.newObserverTitle');

    const rolesToShow = (data?.roles || []).filter((document: any) => {
        const original_document = documents.find((i) => i.document_id === document?.document_id);
        return original_document?.ownership?.includes?.(tab || -1);
    });

    return (
        <PopUpWrapper
            name="UPDATE_PARTICIPANT"
            whiteCross={true}
            onClickOutside={handlers.onClickOutside}
            className="update-participant-modal-dialog"
            size="lg"
        >
            <CommunityTab
                title={
                    <H1 extra="margin: 0 !important; display: block; max-width: calc(800px - 60px); text-overflow: ellipsis; overflow: hidden; white-space: nowrap; font-size: 25px !important;">
                        {headerText}
                    </H1>
                }
                closable={false}
                extra="width: 800px; @media (max-width: 1024px) { width: 100%; }"
            >
                <FormProvider {...form}>
                    <Form onSubmit={handleSubmit(onSubmit)} onChange={handlers.onFormChange} style={{ width: '100%' }}>
                        <Form.Row>
                            <Form.Group as={Col} md={6} controlId="first_name">
                                <HookFormControl.Text
                                    name="first_name"
                                    label={t('communityPagePopUp.firstName')}
                                    maxLength={60}
                                    rules={{
                                        required: t('common.widget.participant.invalidFirstname'),
                                    }}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md={6} controlId="middle_name">
                                <HookFormControl.Text
                                    name="middle_name"
                                    label={t('communityPagePopUp.middleName')}
                                    maxLength={60}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} md={8} controlId="last_name">
                                <HookFormControl.Text
                                    name="last_name"
                                    label={t('communityPagePopUp.lastName')}
                                    maxLength={60}
                                    rules={{
                                        required: t('common.widget.participant.invalidLastname'),
                                    }}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md={4} controlId="suffix">
                                <HookFormControl.Suffix name="suffix" label={t('communityPagePopUp.suffix')} />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="email">
                                <HookFormControl.Email
                                    name="email"
                                    label={t('communityPagePopUp.email')}
                                    disabled={data?.age < 13 || data?.approx_age === 0}
                                    rules={{ validate: handlers.validateEmail }}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md={4} controlId="phone_number">
                                <HookFormControl.Phone
                                    name="phone_number"
                                    label={t('communityPagePopUp.phoneNumber')}
                                    disabled={data?.age < 13 || data?.approx_age === 0}
                                    rules={{ validate: handlers.validatePhoneNumber }}
                                    allowNonUs={allowNonUs}
                                />
                            </Form.Group>
                        </Form.Row>
                        {data.isObserver && (
                            <Form.Row>
                                <Form.Group as={Col} controlId="company">
                                    <HookFormControl.Text
                                        name="company"
                                        label={t('communityPagePopUp.company')}
                                        maxLength={256}
                                    />
                                </Form.Group>
                                <Form.Group as={Col} controlId="observer_relationship">
                                    <HookFormControl.ObserverRelationship
                                        name="observer_relationship"
                                        label={t('communityPagePopUp.observerRelationship')}
                                        maxLength={50}
                                        rules={{
                                            required: t('communityPagePopUp.observerRelationshipRequired'),
                                        }}
                                        onChange={handlers.updateRelationship}
                                    />
                                </Form.Group>
                            </Form.Row>
                        )}
                        {!data.isObserver && (
                            <Form.Row>
                                <Form.Group as={Col} controlId="date_of_birth">
                                    <HookFormControl.Date
                                        name="date_of_birth"
                                        label={t('communityPagePopUp.dateOfBirth')}
                                        rules={{ validate: handlers.validateDateOfBirth }}
                                        onChange={handlers.onDateOfBirthChange}
                                    />
                                </Form.Group>
                                {!data.isSpouse && (
                                    <Form.Group as={Col} controlId="approx_age">
                                        <HookFormControl.ApproximateAge
                                            name="approx_age"
                                            label={t('communityPagePopUp.approximateAge')}
                                            rules={{ validate: handlers.validateApproxAge }}
                                            onChange={handlers.onApproxAgeChange}
                                        />
                                    </Form.Group>
                                )}
                            </Form.Row>
                        )}
                        {!data.isObserver && (
                            <>
                                <Form.Row>
                                    <Form.Group as={Col} sm={5} controlId={`us_resident`} style={{ marginBottom: 0 }}>
                                        {allowNonUs && (
                                            <HookFormControl.Check
                                                name={`us_resident`}
                                                onClick={handlers.onUsResidentClick}
                                                label={t('common.widget.participant.usResident')}
                                            />
                                        )}
                                    </Form.Group>
                                </Form.Row>
                                <Form.Row>
                                    <Form.Group as={Col} md={7} controlId="address.domicile.address_line1">
                                        {isUsResident && (
                                            <HookFormControl.Autocomplete
                                                name="address.domicile.address_line1"
                                                label={t('communityPagePopUp.addressLine_1')}
                                                items={addressSuggestions}
                                                getItemValue={(item: AddressSuggestion) => {
                                                    return item.street_line;
                                                }}
                                                renderItem={(item: AddressSuggestion, isHighlighted: boolean) => (
                                                    <div
                                                        key={handlers.getFullAddressLine(item)}
                                                        style={{ background: isHighlighted ? 'lightgray' : 'white' }}
                                                    >
                                                        {handlers.getFullAddressLine(item)}
                                                    </div>
                                                )}
                                                onChange={delayedSearch}
                                                onSelect={handlers.useSuggestedAddress}
                                            />
                                        )}
                                        {!isUsResident && (
                                            <HookTextFormControl
                                                name={`address.domicile.address_line1`}
                                                label={t('communityPagePopUp.addressLine_1')}
                                                maxLength={60}
                                            />
                                        )}
                                    </Form.Group>

                                    <Form.Group as={Col} controlId="address.domicile.address_line2">
                                        <HookFormControl.Text
                                            name="address.domicile.address_line2"
                                            label={t('communityPagePopUp.addressLine_2')}
                                            maxLength={60}
                                        />
                                    </Form.Group>
                                </Form.Row>
                            </>
                        )}
                        {!data.isObserver && (
                            <>
                                <Form.Row>
                                    <Form.Group as={Col} md={5} controlId="address.domicile.city">
                                        <HookFormControl.Text
                                            name="address.domicile.city"
                                            label={t('communityPagePopUp.city')}
                                            maxLength={60}
                                        />
                                    </Form.Group>
                                    {isUsResident && (
                                        <Form.Group as={Col} md={5} controlId="address.domicile.state_code">
                                            <HookFormControl.State
                                                name="address.domicile.state_code"
                                                label={t('communityPagePopUp.state')}
                                                rules={
                                                    addressWasUpdated && {
                                                        required: t('common.widget.participant.invalidState'),
                                                    }
                                                }
                                            />
                                        </Form.Group>
                                    )}
                                    {!isUsResident && (
                                        <Form.Group as={Col} controlId="address.domicile.region">
                                            <HookTextFormControl
                                                name={`address.domicile.region`}
                                                label={t('common.widget.participant.region')}
                                                maxLength={60}
                                            />
                                        </Form.Group>
                                    )}
                                    <Form.Group as={Col} md={2} controlId="address.domicile.zip">
                                        <HookFormControl.Text
                                            name="address.domicile.zip"
                                            label={
                                                isUsResident
                                                    ? t('common.widget.participant.zip')
                                                    : t('common.widget.participant.zipPostal')
                                            }
                                            pattern={isUsResident ? '(^\\d{5}$)|(^\\d{5}-\\d{4}$)' : '*'}
                                            maxLength={10}
                                            className="zip"
                                        />
                                    </Form.Group>
                                </Form.Row>
                                {!isUsResident && (
                                    <Form.Row>
                                        <Form.Group as={Col} md={6} controlId="address.domicile.country_code">
                                            <HookCountryFormControl
                                                name={`address.domicile.country_code`}
                                                label={t('common.widget.participant.country')}
                                                rules={
                                                    addressWasUpdated && {
                                                        required: t('common.widget.participant.invalidCountry'),
                                                    }
                                                }
                                            />
                                        </Form.Group>
                                    </Form.Row>
                                )}
                            </>
                        )}

                        {!isAdvisor && !!data.roles?.length && (
                            <>
                                <Divider />
                                <NotificationsPlateHeader extra="border: 0px;">
                                    <Frame extra="/*font-family: Open Sans;*/ font-style: normal; font-weight: bold; font-size: 18px; line-height: 25px;">
                                        {t('communityPage.documents')}
                                    </Frame>
                                    <SupportHeading>{t('communityPage.documentStatus')}</SupportHeading>
                                    <SupportHeading>{t('communityPage.notify')}</SupportHeading>
                                    <SupportHeading>{t('communityPage.shareDocuments')}</SupportHeading>
                                </NotificationsPlateHeader>
                                {data.isObserver && (
                                    <NotificationsPlate extra="border: none; flex-direction: row; justify-content: space-around;">
                                        <div className="d-none d-md-block" />
                                        <SupportHeading extra="font-family: unset; margin-right: 10px !important;">
                                            {t('communityPage.selectAll')}
                                        </SupportHeading>
                                        <NotifyShareRow extra="margin-top: 0 !important;">
                                            <CheckboxUpdateFrame>
                                                <span className="d-md-none">{t('communityPage.notify')}</span>
                                                <Checkbox
                                                    selected={allNotifySelected}
                                                    muted={!isParticipantNotifiable}
                                                    onClick={handlers.selectAllNotify}
                                                />
                                            </CheckboxUpdateFrame>
                                            <CheckboxUpdateFrame>
                                                <span className="d-md-none">{t('communityPage.share')}</span>
                                                <Checkbox
                                                    selected={allShareSelected}
                                                    muted={!isParticipantNotifiable}
                                                    onClick={handlers.selectAllShare}
                                                />
                                            </CheckboxUpdateFrame>
                                        </NotifyShareRow>
                                    </NotificationsPlate>
                                )}
                                {rolesToShow.map((document: any = {}, index: number) => {
                                    const isRoleNotifiable = ParticipantsService.isNotifiableRole(data, document);
                                    return (
                                        <NotificationsPlate key={index}>
                                            <Frame>
                                                <DocumentNameFrame>{document.document_name}</DocumentNameFrame>
                                                <RoleFrame>
                                                    <span>{t('communityPage.role')}</span>
                                                    <span>{t(`participantRole.${document?.role_type_code}`)}</span>
                                                </RoleFrame>
                                            </Frame>
                                            <StatusFrame>
                                                {t(`document_status_code.${document?.document_status_code}`)}
                                            </StatusFrame>
                                            <NotifyShareRow>
                                                <CheckboxUpdateFrame>
                                                    <span>{t('communityPage.notify')}</span>
                                                    <Checkbox
                                                        selected={_.get(data, `roles[${index}].notify`) === true}
                                                        muted={!isRoleNotifiable}
                                                        onClick={handlers.onNotifyClick(index)}
                                                    />
                                                </CheckboxUpdateFrame>
                                                <CheckboxUpdateFrame>
                                                    <span>{t('communityPage.share')}</span>
                                                    <Checkbox
                                                        selected={
                                                            _.get(data, `roles[${index}].visibility_code`) ===
                                                            'full_doc'
                                                        }
                                                        muted={!isRoleNotifiable}
                                                        onClick={handlers.onShareClick(index)}
                                                    />
                                                </CheckboxUpdateFrame>
                                            </NotifyShareRow>
                                        </NotificationsPlate>
                                    );
                                })}
                                <P>{t('communityPagePopUp.afterWords')}</P>
                                <Divider />
                                {!data.isObserver && (
                                    <P extra="margin-bottom: 25px;">{t('communityPagePopUp.warningLabel')}</P>
                                )}
                            </>
                        )}
                        {data.isObserver && (
                            <div style={{ marginBottom: '25px' }}>
                                <P extra="margin-bottom: 10px;">
                                    <HText>{t('communityPagePopUp.observerNote1')}</HText>
                                </P>
                                <P>
                                    <HText>{t('communityPagePopUp.observerNote2')}</HText>
                                </P>
                            </div>
                        )}
                        <div className="w-100 d-flex justify-content-end">
                            <Button
                                data-qaid="cancel_button"
                                className="btn btn-light pl-0 pr-2 pl-sm-2"
                                onClick={handlers.onCancelClick}
                            >
                                {t('communityPagePopUp.cancel')}
                            </Button>
                            <Button
                                data-qaid="save_button"
                                type="submit"
                                className="ml-sm-3 btn-primary_action"
                                disabled={loading}
                            >
                                {t(data.participant_id ? 'communityPagePopUp.update' : 'communityPagePopUp.save')}
                            </Button>
                        </div>
                    </Form>
                </FormProvider>
            </CommunityTab>
        </PopUpWrapper>
    );
};

export default UpdateParticipantModal;
